/**
 * Deterministic, serialisable pseudo-random generator (mulberry32).
 *
 * The 2013 version called `srand(time(NULL))` in several places
 * (`JeuConstructeur`, `PlaceBonus`), reseeding the RNG mid-game and breaking any
 * reproducibility. Here we seed once at round start and thread the state through,
 * which makes the simulation replayable (handy for tests and future netcode).
 */
export class Rng {
  private state: number;

  constructor(seed: number) {
    this.state = seed >>> 0;
  }

  /** Next float in [0, 1). */
  next(): number {
    this.state = (this.state + 0x6d2b79f5) | 0;
    let t = this.state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Integer in [0, maxExclusive). Equivalent to `rand() % maxExclusive`. */
  int(maxExclusive: number): number {
    return Math.floor(this.next() * maxExclusive);
  }

  /** Integer in [min, max] inclusive. */
  range(min: number, max: number): number {
    return min + this.int(max - min + 1);
  }

  /** Internal state, for save / replay. */
  snapshot(): number {
    return this.state;
  }

  restore(state: number): void {
    this.state = state >>> 0;
  }
}
